import { FontStyle, FontWeight } from '@nolimitcity/slot-launcher/bin/loader/font/FontStatics';
import { gsap, Linear, Power2, Elastic, TimelineLite } from 'gsap';
import { ReelSpriteSymbol } from '../../../core/reel/symbol/ReelSpriteSymbol';
import { ReelSymbolStates } from '../../../core/reel/symbol/ReelSpriteSymbolArea';
import { TextStyleOptions } from '../../../core/setting/text/IText';
import { Helper } from '../../../core/utils/Helper';
import { Beheaded } from '../../Beheaded';
import { SpinePlayer } from '../../../core/displayobject/SpinePlayer';
import { BasicSymbolType, ISymbolConfig } from '../../../core/reel/symbol/config/IReelSymbolStruct';
import { SymbolData } from '../../../core/reel/symbol/config/SymbolData';
import { BLEND_MODES } from 'pixi.js';
import { SoundManager } from '../../config/sound/SoundManager';
import { SoundConfig } from '../../config/sound/SoundConfig';

export class BeheadedSymbol extends ReelSpriteSymbol {
    private readonly _styleWays: TextStyleOptions = {
        fontFamily: 'Helvetica',
        fontStyle: FontStyle.ITALIC,
        fontSize: 64,
        fontWeight: FontWeight.EXTRA_BOLD,
        dropShadow: true,
        dropShadowAngle: 8,
        dropShadowBlur: 8,
        dropShadowDistance: 8,
        fill: '#FFDD02',
        lineJoin: 'round',
        align: 'center',
        stroke: '#000000',
        strokeThickness: 8,
    };

    private scatterSym: SpinePlayer;
    private winSym: SpinePlayer;
    private chargeSym: SpinePlayer;
    public rope: PIXI.Sprite;

    public fx: PIXI.Sprite;
    public fxDrop: PIXI.Sprite;

    private get fetchSpritePath(): string {
        if (this.isWild) return this.symName;
        return this.isLowValue ? this.symName : 'M_H' + this.symName[1];
    }

    public get isHighValSym(): boolean {
        return (
            this.symName.length == 2 &&
            !isNaN(parseInt(this.symName[1])) &&
            (this.symName[0] === 'M' || this.symName[0] === 'H')
        );
    }

    public get visualItem(): PIXI.Sprite | SpinePlayer {
        return this.chargeSym ? (this.winSym ? this.winSym : this.chargeSym) : this.mainSp;
    }

    public setPosID(value: number, reverse?: boolean) {
        super.setPosID(value, reverse);
    }

    constructor(ID: number, symName: string, state: string = ReelSymbolStates.NORMAL, type?: number) {
        super(ID, symName, state, type);
    }

    public setSymbol(symName: string): void {
        if (symName.includes('H')) {
            let newConfig: ISymbolConfig | undefined = SymbolData.getConfig(symName);
            this.symbolConfig = newConfig;
            this.scale = this.dim ? this.dim.SCALE : new PIXI.Point(1, 1);
            this.chargeSymbolInit();
        } else if (symName == BasicSymbolType.SCATTER) {
            let newConfig: ISymbolConfig | undefined = SymbolData.getConfig(symName);
            this.symbolConfig = newConfig;
            this.scatterSym = this.addChild(new SpinePlayer('Scatter', true)); // Landing, hit, idle, idle2
            this.mainSp.visible = false;
        } else {
            super.setSymbol(symName);
            this.fx = this.addChild(new PIXI.Sprite(this.mainSp.texture));
            this.fx.blendMode = BLEND_MODES.ADD;
            this.fx.anchor.set(0.5);
            this.fx.alpha = 0.2;
            // this.fx.tint = 0x000000;
        }
    }

    private chargeSymbolInit(): void {
        this.chargeSym = this.addChild(new SpinePlayer(this.fetchSpritePath, true));
        this.chargeSym.play({ animation: 'charged_loop', loop: true });

        this.mainSp.texture = SymbolData.getTextures('M' + this.symName[1], this.state.value, this.sizeType.value)![0];
        this.mainSp.visible = false;
    }

    public chargeSymbol(targetLevel?: number): void {
        if (!this.isHighValSym) return;
        if (targetLevel != undefined) {
            const threshold = [6, 5, 4, 3, 2, 1][targetLevel];
            const typeLevel = parseInt(this.symName[1]);
            if (threshold > typeLevel) return;
        }

        if (!this.chargeSym && this.isHighValSym) {
            this.chargeSym = this.addChild(new SpinePlayer(this.fetchSpritePath, true));
            this.chargeSym.play({
                animation: this.isWild ? 'Win' : 'charged',
                showOnEnd: true,
                onComplete: () => {
                    this.chargeSym.play({ animation: this.isWild ? 'Win_loop' : 'charged_loop', loop: true });
                },
            });
        } else if (this.chargeSym) {
            this.chargeSym.visible = true;
            this.chargeSym.changeSpine(this.fetchSpritePath);
        }

        this.mainSp.visible = false;
    }

    public deChargeSymbol(level: number): void {
        if (this.isHighValSym) {
            const threshold = [6, 5, 4, 3, 2, 1][level];
            const typeLevel = parseInt(this.symName[1]);
            if (threshold >= typeLevel) {
                this.mainSp.visible = true;
                if (this.chargeSym) {
                    this.chargeSym.visible = false;
                }
            }
        }
    }

    private wild: SpinePlayer;

    public convertToWild(): gsap.core.Timeline {
        this.changeSymbol('W');

        this.wild = this.addChild(new SpinePlayer('spine/W_piece_spawn.json', true));

        this.mainSp.visible = false;
        if (this.chargeSym) this.chargeSym.visible = false;
        if (this.scatterSym) this.scatterSym.visible = false;

        this.wild.play({
            animation: 'W',
            showOnEnd: true,
            duration: 0.25,
            onComplete: () => {
                try {
                    this.wild.play({ animation: 'W_loop', loop: true });
                } catch {
                    // Trying to find a potential race condition
                    debugger;
                    if (this.wild.spine && this.wild.parent) this.wild.play({ animation: 'W_loop', loop: true });
                }
            },
        });

        return gsap.timeline();
    }

    public applyRope(sp: PIXI.Sprite): void {
        sp.position.y = 0;
        sp.zIndex = -5;
        this.rope = this.addChild(sp);
    }

    playCut() {
        const spine = Beheaded.overlayAnim.playStandAloneSpine('spine/rope_burn.json', {
            animation: 'burn_2',
            duration: 0.25,
        }).item;
        spine.position = Helper.copyLocalPosition(spine, this);
        spine.position.y -= this.symbolHeight / 2;
        spine.scale.set(0.75);
    }

    public onSpinStart(): void {
        if (this.winSym) {
            this.StopWinSymbol(0.25);
        }
    }

    applyDropFx(durr: number, toAlpha:number = 1): void {
        if (!this.fxDrop) {
            this.fxDrop = this.addChild(new PIXI.Sprite(this.mainSp.texture));
            this.fxDrop.anchor.set(0.5);
            this.fxDrop.tint = 0x000000;
            this.fxDrop.alpha = 0;
        }
        if(this.fx)
            gsap.to(this.fx, { duration: durr, alpha: 0, delay: ((4 - this.posID[0]) * durr) / 4 });
        gsap.to(this.fxDrop, { duration: durr, alpha: toAlpha, delay: ((4 - this.posID[0]) * durr) / 4 });
    }

    onLanded(): void {
        if (this.isScatter) {
            this.scatterSym.play({
                animation: 'idle',
                showOnEnd: true,
                onComplete: () => {
                    // this.scatterSym.play({ animation:"idle2", loop:true, delay:2});
                },
            });
        }

        if (this.fx) {
            gsap.fromTo(this.fx, { alpha: 0 }, { alpha: 0.2, duration: 0.4 });
        }
    }

    public markNonWin(enable: boolean, alpha: number = 0.55, durr: number = 0.25): void {
        // if (this.isScatter || this.isMystery) {
        //     super.markNonWin(false, alpha, durr)
        // } else {
        //     super.markNonWin(enable, alpha, durr);
        // }
    }

    // public setInteractive(val: boolean): void {
    //     if (this.isWild || this.isScatter) return;

    //     if (this.mainSp) {
    //         this.mainSp!.interactive = val;
    //         this.mainSp!.on('pointerdown', () => {
    //             MiniPayTable.instance.onClicked({
    //                 dispObj: this.mainSp,
    //                 name: this.symName,
    //             });
    //         });
    //     }
    // }

    public fetchWinDuration(isLoop: boolean): number {
        if (this.symName == BasicSymbolType.SCATTER) return SpinePlayer.fetchDuration('M_H1', isLoop ? 'Win_loop' : 'Win');
        return SpinePlayer.fetchDuration(this.fetchSpritePath, isLoop ? 'Win_loop' : 'Win');
    }

    private tlRotation: gsap.core.Timeline;
    playWin(loop: boolean = false, offset: number = 0, isBigWin: boolean = false): void {
        if (this.isScatter) {
            return;
        } else if (this.useWinSymbol(true)) {
            let dur: number = this.fetchWinDuration(loop);
            offset = offset / 2 < 0.15 ? 0.1 : offset / 2;

            this.tlRotation = Helper.setTimeline(
                this.tlRotation,
                gsap.timeline({
                    repeat: isBigWin && !loop ? 15 : 0,
                    onStart: () => {
                        this.mainSp.visible = false;
                        if (this.chargeSym) this.chargeSym.visible = false;
                    },
                })
            );

            if (loop) {
                this.tlRotation.to(this.winSym.scale, { x: 1.15, y: 1.15, duration: dur / 2 });
                this.tlRotation.to(this.winSym.scale, { x: 1, y: 1, duration: dur / 2 });

                if (!this.winSym.isPlaying) this.winSym.play({ animation: 'Win_loop', loop: true });
            } else {
                this.tlRotation.to(this.winSym, {
                    duration: isBigWin ? 0.06 : 0.1,
                    rotation: isBigWin ? -0.2 : -0.09,
                    ease: (isBigWin ? Linear.easeInOut : Elastic.easeInOut) as any,
                });
                this.tlRotation.to(this.winSym, {
                    duration: isBigWin ? 0.05 : 0.08,
                    rotation: 0,
                    ease: (isBigWin ? Linear.easeInOut : Elastic.easeInOut) as any,
                });
                if (isBigWin) {
                    this.tlRotation.to(this.winSym, {
                        duration: 0.06,
                        rotation: 0.2,
                        ease: Linear.easeInOut as any,
                    });
                    this.tlRotation.to(this.winSym, {
                        duration: 0.05,
                        rotation: 0,
                        ease: Linear.easeInOut as any,
                    });
                }

                SoundManager.playRandomFromList(isBigWin ? SoundConfig.WILD_HIGHLIGHT_BIG : SoundConfig.WILD_HIGHLIGHT_SMALL)

                this.winSym.play({
                    animation: 'Win',
                    showOnEnd: true,
                    onComplete: () => {
                        this.winSym.play({ animation: 'Win_loop', loop: true });
                    },
                });
            }
        }
    }

    shake(): void {
        this.tlRotation = Helper.setTimeline(this.tlRotation);
        this.tlRotation.to(this, {
            duration: 0.06,
            rotation: -0.2,
            ease: Linear.easeInOut as any,
        });
        this.tlRotation.to(this, {
            duration: 0.05,
            rotation: 0,
            ease: Linear.easeInOut as any,
        });
        this.tlRotation.to(this, {
            duration: 0.06,
            rotation: 0.2,
            ease: Linear.easeInOut as any,
        });
        this.tlRotation.to(this, {
            duration: 0.05,
            rotation: 0,
            ease: Linear.easeInOut as any,
        });
    }

    private useWinSymbol(shouldBeCreated: boolean = true): boolean {
        if (!this.winSym && this.mainSp && shouldBeCreated) {
            if (!this.fetchSpritePath) debugger;
            this.winSym = new SpinePlayer(this.fetchSpritePath);
            this.addChild(this.winSym);
        }
        return !!this.winSym;
    }

    stopWin(duration: number, ignoreDarknessChange?: boolean) {
        if (this.isScatter) return;
        if (this.useWinSymbol(false)) {
            this.StopWinSymbol(duration);
        }
        if (!ignoreDarknessChange) this.markNonWin(false, undefined, duration);
    }

    public setVisible(value: boolean): void {
        super.setVisible(value);
        if (this.winSym) {
            this.winSym.visible = value;
        }
    }

    public onNewFromAvalanche(durr: number = 0.2): void {
        gsap.to(this, { alpha: 1, duration: durr });
    }

    remove(): void {
        if (this.tlRotation) {
            if (this.tlRotation.isActive()) debugger;
            this.tlRotation.progress(1);
        }

        [this.winSym, this.chargeSym, this.scatterSym, this.wild].forEach((sym) => {
            if (sym) {
                sym.stop(true);
                sym.destroy();
                sym = null!;
            }
        });
        super.remove();
    }

    StopWinSymbol(duration: number): gsap.core.Timeline {
        this._timeLine = Helper.setTimeline(
            this._timeLine,
            new gsap.core.Timeline({
                onComplete: () => {
                    this.winSym!.visible = false;
                },
            }),
            true
        );
        this._timeLine.fromTo(this.winSym, { alpha: 1 }, { alpha: 0, duration: duration }, 0);
        return this._timeLine;
    }
}
